import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from '../firebase';

type DesignConfig = {
  primaryColor: string;
  accentColor: string;
  logoUrl?: string;
  bannerUrl?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  buttonRadius?: number;
  footerColor?: string;
  logoColor?: string;
  buttonColor?: string;
  faviconUrl?: string;
  homeImages?: string[];
  headerColor?: string;
  servicesSectionColor?: string;
  allCategoryImageUrl?: string;
  supportPhone?: string;
  servicesContent?: {
    icon: string;
    title: string;
    description: string;
  }[];
  aboutUs?: {
    title?: string;
    description?: string;
    imageUrl?: string;
  };
  footer?: {
    description?: string;
    contact?: {
      phone?: string;
      email?: string;
      hours?: string;
    };
    socialLinks?: {
      facebook?: string;
      instagram?: string;
      whatsapp?: string;
    };
  };
};

type DesignContextValue = {
  config: DesignConfig;
  theme: ReturnType<typeof createTheme>;
};

const defaultConfig: DesignConfig = {
  primaryColor: '#e91e63',
  accentColor: '#c2185b',
  logoUrl: undefined,
  heroTitle: undefined,
  heroSubtitle: undefined,
  bannerUrl: undefined,
  buttonRadius: 12,
  footerColor: '#121212',
  logoColor: '#ffffff',
  buttonColor: undefined,
  faviconUrl: undefined,
  headerColor: '#a733ff',
  servicesSectionColor: '#a733ff',
  allCategoryImageUrl: 'https://images.unsplash.com/photo-1490750967868-58cb75069faf?w=150',
  supportPhone: '522222222222',
  servicesContent: [
    { icon: 'speed', title: 'Envío Express', description: 'Entrega en 2 horas en zonas seleccionadas' },
    { icon: 'palette', title: 'Diseño Exclusivo', description: 'Arreglos únicos creados por expertos floristas' },
    { icon: 'flower', title: 'Frescura Garantizada', description: 'Seleccionamos las flores más frescas cada día' }
  ],
  aboutUs: {
    title: 'Pasión por los detalles',
    description: 'En Blüm, entendemos que cada flor cuenta una historia. Desde hace más de 10 años, nos dedicamos a seleccionar las flores más frescas y hermosas para crear arreglos que expresan lo que las palabras a veces no pueden. Nuestro compromiso es entregar no solo flores, sino emociones, directamente a la puerta de tus seres queridos.',
    imageUrl: 'https://images.unsplash.com/photo-1526047932273-341f2a7631f9?w=800'
  },
  footer: {
    description: 'Los mejores arreglos florales con entrega a domicilio en México. Flores frescas y diseños exclusivos para cada ocasión especial.',
    contact: {
      phone: '+52 2222222222',
      email: 'soporte@blum-art.mx',
      hours: 'Lunes a Domingo 9:00 - 20:00 hrs'
    },
    socialLinks: {
      facebook: '',
      instagram: '',
      whatsapp: ''
    }
  }
};

const DesignContext = createContext<DesignContextValue>({
  config: defaultConfig,
  theme: createTheme({
    palette: {
      primary: { main: defaultConfig.primaryColor },
      secondary: { main: defaultConfig.accentColor },
      background: { default: '#f5f5f5' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 600 },
    },
    shape: { borderRadius: defaultConfig.buttonRadius ?? 12 },
  }),
});

export const DesignProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Hydrate initial config from localStorage to avoid initial flash of default colors
  const getInitialConfig = (): DesignConfig => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('designConfig') : null;
      if (!raw) return defaultConfig;
      const data = JSON.parse(raw || '{}') || {};
      const initial: DesignConfig = {
        primaryColor: typeof data.primaryColor === 'string' ? data.primaryColor : defaultConfig.primaryColor,
        accentColor: typeof data.accentColor === 'string' ? data.accentColor : defaultConfig.accentColor,
        logoUrl: typeof data.logoUrl === 'string' ? data.logoUrl : undefined,
        bannerUrl: typeof data.bannerUrl === 'string' ? data.bannerUrl : undefined,
        heroTitle: typeof data.heroTitle === 'string' ? data.heroTitle : undefined,
        heroSubtitle: typeof data.heroSubtitle === 'string' ? data.heroSubtitle : undefined,
        buttonRadius: typeof data.buttonRadius === 'number' ? data.buttonRadius : defaultConfig.buttonRadius,
        footerColor: typeof data.footerColor === 'string' ? data.footerColor : defaultConfig.footerColor,
        logoColor: typeof data.logoColor === 'string' ? data.logoColor : defaultConfig.logoColor,
      buttonColor: typeof data.buttonColor === 'string' ? data.buttonColor : defaultConfig.buttonColor,
        faviconUrl: typeof data.faviconUrl === 'string' ? data.faviconUrl : undefined,
        headerColor: typeof data.headerColor === 'string' ? data.headerColor : defaultConfig.headerColor,
        servicesSectionColor: typeof data.servicesSectionColor === 'string' ? data.servicesSectionColor : defaultConfig.servicesSectionColor,
        allCategoryImageUrl: typeof data.allCategoryImageUrl === 'string' ? data.allCategoryImageUrl : defaultConfig.allCategoryImageUrl,
        supportPhone: typeof data.supportPhone === 'string' ? data.supportPhone : defaultConfig.supportPhone,
        servicesContent: Array.isArray(data.servicesContent) ? data.servicesContent : defaultConfig.servicesContent,
        aboutUs: data.aboutUs || defaultConfig.aboutUs,
        footer: data.footer || defaultConfig.footer,
      };
      return initial;
    } catch {
      return defaultConfig;
    }
  };

  const [config, setConfig] = useState<DesignConfig>(getInitialConfig());

  // Effect to update favicon dynamically
  useEffect(() => {
    if (config.faviconUrl) {
      const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = config.faviconUrl;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [config.faviconUrl]);

  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(doc(db, 'settings', 'design'), (snap) => {
      const data = snap.exists() ? (snap.data() as any) : {};
      const next: DesignConfig = {
        primaryColor: typeof data.primaryColor === 'string' ? data.primaryColor : defaultConfig.primaryColor,
        accentColor: typeof data.accentColor === 'string' ? data.accentColor : defaultConfig.accentColor,
        logoUrl: typeof data.logoUrl === 'string' ? data.logoUrl : undefined,
        bannerUrl: typeof data.bannerUrl === 'string' ? data.bannerUrl : undefined,
        heroTitle: typeof data.heroTitle === 'string' ? data.heroTitle : undefined,
        heroSubtitle: typeof data.heroSubtitle === 'string' ? data.heroSubtitle : undefined,
        buttonRadius: typeof data.buttonRadius === 'number' ? data.buttonRadius : defaultConfig.buttonRadius,
        footerColor: typeof data.footerColor === 'string' ? data.footerColor : defaultConfig.footerColor,
        logoColor: typeof data.logoColor === 'string' ? data.logoColor : defaultConfig.logoColor,
        buttonColor: typeof data.buttonColor === 'string' ? data.buttonColor : defaultConfig.buttonColor,
        faviconUrl: typeof data.faviconUrl === 'string' ? data.faviconUrl : undefined,
        homeImages: Array.isArray(data.homeImages) ? data.homeImages : [],
        headerColor: typeof data.headerColor === 'string' ? data.headerColor : defaultConfig.headerColor,
        servicesSectionColor: typeof data.servicesSectionColor === 'string' ? data.servicesSectionColor : defaultConfig.servicesSectionColor,
        allCategoryImageUrl: typeof data.allCategoryImageUrl === 'string' ? data.allCategoryImageUrl : defaultConfig.allCategoryImageUrl,
        supportPhone: typeof data.supportPhone === 'string' ? data.supportPhone : defaultConfig.supportPhone,
        servicesContent: Array.isArray(data.servicesContent) ? data.servicesContent : defaultConfig.servicesContent,
        aboutUs: data.aboutUs || defaultConfig.aboutUs,
        footer: data.footer || defaultConfig.footer,
      };
      setConfig(next);
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('designConfig', JSON.stringify(next));
        }
      } catch {}
    }, (err) => {
      console.error('[Design] Error suscribiendo diseño. Verifique que las reglas de Firestore permitan leer "settings/design" públicamente.', err);
      // Mantener configuración actual o usar defaults
      setConfig((prev) => prev || defaultConfig);
    });
    return () => unsub();
  }, []);

  const theme = useMemo(() => {
    const primaryMain = config.buttonColor || config.primaryColor;
    const palette: any = {
      primary: {
        main: primaryMain,
        dark: primaryMain,
      },
      secondary: {
        main: config.accentColor,
        dark: config.accentColor,
      },
      background: { default: '#f5f5f5' },
      // Custom palette keys for footer and logo/icon colors
      custom: {
        footerBg: config.footerColor,
        logoColor: config.logoColor,
      },
    };
    return createTheme({
      palette,
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h1: { fontWeight: 700 },
        h2: { fontWeight: 600 },
      },
      shape: { borderRadius: config.buttonRadius ?? 12 },
    } as any);
  }, [config.primaryColor, config.accentColor, config.buttonRadius, config.footerColor, config.logoColor, config.buttonColor]);

  const value: DesignContextValue = { config, theme };

  return (
    <DesignContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </DesignContext.Provider>
  );
};

export const useDesign = () => useContext(DesignContext);